import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Navbar, Footer } from '../components/Layout';
import { CartContext, API } from '../App';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  
  const category = searchParams.get('category') || 'all';
  const sort = searchParams.get('sort') || '';
  const search = searchParams.get('search') || '';
  
  const { addToCart } = useContext(CartContext);


  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/products?`;
      if (category && category !== 'all') url += `category=${category}&`;
      if (sort) url += `sort=${sort}&`;
      if (search) url += `search=${search}&`;
      
      const response = await fetch(url);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [category, sort, search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleCategoryChange = (value) => {
    setSearchParams(params => {
      if (value === 'all') {
        params.delete('category');
      } else {
        params.set('category', value);
      }
      return params;
    });
  };

  const handleSortChange = (value) => {
    setSearchParams(params => {
      if (value === 'default') {
        params.delete('sort');
      } else {
        params.set('sort', value);
      }
      return params;
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchValue = formData.get('search');
    setSearchParams(params => {
      if (searchValue) {
        params.set('search', searchValue);
      } else {
        params.delete('search');
      }
      return params;
    });
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  const hasActiveFilters = category !== 'all' || sort || search;

  return (
    <div className="min-h-screen bg-white" data-testid="products-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-12 bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#C4A882] mb-3">Our Collection</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A1A1A] tracking-tight">
            Products
          </h1>
          
          {/* Filters Bar */}
          <div className="mt-8 flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="w-full lg:w-auto lg:flex-1 lg:max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  name="search"
                  placeholder="Search products..."
                  defaultValue={search}
                  className="pl-10 rounded-none border-[#E5E5E5] focus:border-[#C4A882] focus:ring-[#C4A882]"
                  data-testid="search-input"
                />
              </div>
            </form>
            
            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center gap-4">
              <Select value={category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[180px] rounded-none" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Home">Home Furniture</SelectItem>
                  <SelectItem value="Office">Office Furniture</SelectItem>
                  <SelectItem value="Custom">Custom & Modular</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sort || 'default'} onValueChange={handleSortChange}>
                <SelectTrigger className="w-[180px] rounded-none" data-testid="sort-filter">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Newest First</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
              
              {hasActiveFilters && (
                <button 
                  onClick={clearFilters}
                  className="text-sm text-[#666666] hover:text-[#1A1A1A] flex items-center"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear filters
                </button>
              )}
            </div>
            
            {/* Mobile Filter Toggle */}
            <button 
              className="lg:hidden flex items-center text-sm font-medium"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-[#C4A882] rounded-full"></span>}
            </button>
          </div>
          
          {/* Mobile Filters */}
          {showFilters && (
            <div className="lg:hidden mt-4 p-4 bg-white border border-[#E5E5E5] space-y-4">
              <Select value={category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full rounded-none">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Home">Home Furniture</SelectItem>
                  <SelectItem value="Office">Office Furniture</SelectItem>
                  <SelectItem value="Custom">Custom & Modular</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sort || 'default'} onValueChange={handleSortChange}>
                <SelectTrigger className="w-full rounded-none">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Newest First</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
              
              {hasActiveFilters && (
                <button 
                  onClick={clearFilters}
                  className="w-full btn-secondary text-sm"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 aspect-[4/3] mb-4"></div>
                  <div className="h-4 bg-gray-200 w-1/3 mb-2"></div>
                  <div className="h-5 bg-gray-200 w-2/3 mb-2"></div>
                  <div className="h-4 bg-gray-200 w-1/4"></div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-heading text-xl text-[#666666] mb-4">No products found</p>
              <button onClick={clearFilters} className="btn-primary text-sm">
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <p className="font-body text-sm text-[#666666] mb-6" data-testid="products-count">
                {products.length} product{products.length !== 1 ? 's' : ''} found
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {products.map((product) => (
                  <div 
                    key={product.id} 
                    className="product-card group"
                    data-testid={`product-card-${product.id}`}
                  >
                    <Link to={`/products/${product.id}`}>
                      <div className="aspect-[4/3] overflow-hidden mb-4 bg-[#F9F9F8]">
                        <img 
                          src={(product.image_urls && product.image_urls.length > 0) ? product.image_urls[0] : (product.image_url || 'https://via.placeholder.com/400x300?text=in-dekor')} 
                          alt={product.name}
                          className="w-full h-full object-cover transition-transform duration-500"
                        />
                      </div>
                    </Link>
                    <p className="font-body text-xs uppercase tracking-[0.15em] text-[#666666] mb-1">
                      {product.category}
                    </p>
                    <Link to={`/products/${product.id}`}>
                      <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-1 group-hover:text-[#C4A882] transition-colors line-clamp-1">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="font-heading text-lg font-semibold text-[#1A1A1A] mb-3">
                      {product.price_on_request ? 'Price on Request' : `₹${product.price.toLocaleString('en-IN')}`}
                    </p>
                    {!product.price_on_request && (
                      <button 
                        onClick={() => handleAddToCart(product)}
                        className="btn-secondary w-full text-sm"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        Add to Cart
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
